import * as Tabs from '@radix-ui/react-tabs';
import { Settings2, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import { AnnotationCard } from './AnnotationCard';
import { AnnotationSettings } from './AnnotationSettings';
import type {
  Annotation,
  AnnotationColor,
  AnnotationFilter,
  AnnotationReference,
  AnnotationSidebarGroup,
  AnnotationTextColor,
} from './annotationTypes';
import styles from './AnnotationsSidebar.module.css';

interface AnnotationsSidebarProps {
  error: string | null;
  filter: AnnotationFilter;
  groups: AnnotationSidebarGroup[];
  highlightColor: AnnotationColor;
  noteTextColor: AnnotationTextColor;
  onClearError: () => void;
  onClose: () => void;
  onDeleteAnnotation: (annotation: Annotation) => Promise<void> | void;
  onFilterChange: (filter: AnnotationFilter) => void;
  onHighlightColorChange: (color: AnnotationColor) => void;
  onNoteTextColorChange: (color: AnnotationTextColor) => void;
  onSelectAnnotation: (
    reference: AnnotationReference,
    options?: { isOrphaned?: boolean },
  ) => void;
  onShowAnnotationsChange: (showAnnotations: boolean) => void;
  open: boolean;
  showAnnotations: boolean;
}

const FILTER_LABELS: Record<AnnotationFilter, string> = {
  all: 'All',
  highlights: 'Highlights',
  notes: 'Notes',
};

export function AnnotationsSidebar({
  error,
  filter,
  groups,
  highlightColor,
  noteTextColor,
  onClearError,
  onClose,
  onDeleteAnnotation,
  onFilterChange,
  onHighlightColorChange,
  onNoteTextColorChange,
  onSelectAnnotation,
  onShowAnnotationsChange,
  open,
  showAnnotations,
}: AnnotationsSidebarProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  if (!open) {
    return null;
  }

  return (
    <Card
      aria-labelledby="annotations-sidebar-title"
      as="aside"
      className={styles.panel}
      variant="elevated"
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <p className={styles.eyebrow}>Study Tools</p>
          <h2 className={styles.title} id="annotations-sidebar-title">
            My annotations
          </h2>
        </div>
        <div className={styles.headerActions}>
          <Button
            aria-label={
              isSettingsOpen
                ? 'Close annotation settings'
                : 'Open annotation settings'
            }
            className={styles.closeButton}
            iconOnly
            onClick={() => setIsSettingsOpen((current) => !current)}
            variant="ghost"
          >
            <Settings2 aria-hidden="true" size={18} />
          </Button>
          <Button
            aria-label="Close annotations"
            className={styles.closeButton}
            iconOnly
            onClick={onClose}
            variant="ghost"
          >
            <X aria-hidden="true" size={18} />
          </Button>
        </div>
      </div>

      {error ? (
        <div className={styles.errorBanner} role="alert">
          <p>{error}</p>
          <Button onClick={onClearError} size="small" variant="secondary">
            Dismiss
          </Button>
        </div>
      ) : null}

      {isSettingsOpen ? (
        <AnnotationSettings
          highlightColor={highlightColor}
          noteTextColor={noteTextColor}
          onHighlightColorChange={onHighlightColorChange}
          onNoteTextColorChange={onNoteTextColorChange}
          onShowAnnotationsChange={onShowAnnotationsChange}
          showAnnotations={showAnnotations}
        />
      ) : null}

      <Tabs.Root
        className={styles.tabs}
        onValueChange={(value) => onFilterChange(value as AnnotationFilter)}
        value={filter}
      >
        <Tabs.List aria-label="Filter annotations" className={styles.tabsList}>
          {(['all', 'highlights', 'notes'] as const).map((value) => (
            <Tabs.Trigger
              className={styles.tabTrigger}
              key={value}
              onClick={() => onFilterChange(value)}
              value={value}
            >
              {FILTER_LABELS[value]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* The list is what the selected tab shows, so it is that tab's panel:
            without one, the tab's aria-controls named nothing. */}
        <Tabs.Content className={styles.tabPanel} value={filter}>
          {groups.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>No annotations yet</p>
              <p className={styles.emptyMessage}>
                Highlight chapter text or add a note to build your personal study guide.
              </p>
            </div>
          ) : (
            <div className={styles.groups}>
              {groups.map((group) => (
                <section className={styles.group} key={group.id}>
                  <h3 className={styles.groupTitle}>{group.label}</h3>
                  <div className={styles.items}>
                    {group.items.map((item) => (
                      <AnnotationCard
                        annotation={item.annotation}
                        isOrphaned={item.isOrphaned}
                        key={item.annotation.id}
                        onDelete={onDeleteAnnotation}
                        onSelect={() =>
                          onSelectAnnotation(
                            {
                              annotationId: item.annotation.id,
                              contentUnitId: item.annotation.contentUnitId,
                            },
                            item.isOrphaned ? { isOrphaned: true } : undefined,
                          )
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </Card>
  );
}
